import { useState } from 'react';

import {
    DndContext,
    DragEndEvent,
    DragOverlay,
    MouseSensor,
    TouchSensor,
    UniqueIdentifier,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Typography } from '@mui/material';

import { DreConfigSubgruposItem } from 'pages/financeiro/dre/configuracao/components';
import { useDreConfigContext } from 'pages/financeiro/dre/configuracao/contexts/DreConfigContext';
import { useSortSubgrupos } from 'pages/financeiro/dre/configuracao/hooks';
import { DreSubgrupo } from 'pages/financeiro/dre/configuracao/types';

export function DreConfigSubgruposList() {
    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
    const { subgrupos, setSubgrupos, isFetchingSubgrupos, isFetchingGrupos } = useDreConfigContext();
    const { updateSort } = useSortSubgrupos();

    // Configurações de sensores para mouse e toque
    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5,
            },
        }),
    );

    /**
     * Ao soltar o item, monta a nova lista de subgrupos com a nova sequência
     * e atualiza a sequência no backend.
     */
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = subgrupos.findIndex((item) => item.idDreMercadologicaGrupoExibicao === active.id);
            const newIndex = subgrupos.findIndex((item) => item.idDreMercadologicaGrupoExibicao === over.id);

            const newSubgrupos = arrayMove(subgrupos, oldIndex, newIndex).map((item, index) => ({
                ...item,
                nrSequencia: index + 1,
            }));

            setSubgrupos(newSubgrupos);

            updateSort(newSubgrupos);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', padding: 1.25, gap: 1.25 }}>
            {(isFetchingSubgrupos || isFetchingGrupos) &&
                Array.from({ length: 5 }).map((_, index) => (
                    <Box
                        key={index}
                        className="pulse"
                        sx={{
                            height: 48,
                            borderWidth: 1,
                            borderRadius: 1,
                            borderColor: 'grey.300',
                            backgroundColor: 'grey.200',
                        }}
                    />
                ))}

            {!isFetchingSubgrupos && !isFetchingGrupos && subgrupos.length === 0 && (
                <Typography sx={{ lineHeight: 10, textAlign: 'center' }}>Nenhum subgrupo adicionado</Typography>
            )}

            {!isFetchingSubgrupos && !isFetchingGrupos && (
                <DndContext
                    sensors={sensors}
                    onDragEnd={handleDragEnd}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    onDragStart={({ active }) => setActiveId(active.id)}
                >
                    <SortableContext
                        items={subgrupos.map((subgrupo) => subgrupo.idDreMercadologicaGrupoExibicao)}
                        strategy={verticalListSortingStrategy}
                    >
                        {subgrupos.map((subgrupo) => (
                            <DreConfigSubgruposItem key={subgrupo.idDreMercadologicaGrupoExibicao} subgrupo={subgrupo} />
                        ))}
                    </SortableContext>

                    {activeId && (
                        <DragOverlay>
                            <DreConfigSubgruposItem
                                isOverlay
                                subgrupo={subgrupos.find((subgrupo) => subgrupo.idDreMercadologicaGrupoExibicao === activeId) as DreSubgrupo}
                            />
                        </DragOverlay>
                    )}
                </DndContext>
            )}
        </Box>
    );
}
